/**
 * Rectangle2DNode — Outputs a filled rectangle as a single StyledPolyline.
 *
 * Useful as a background layer in the 2D presentation view.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { Polylines } from '../TurtleNode/TurtleNode';
import { colorToHex } from '@plugins/threejs/colorUtils';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const rectangle2DEndpoints = {
  ...params({
    width:  { value: 100, min: 0.01, max: 10000, step: 0.01, label: 'Width' },
    height: { value: 100, min: 0.01, max: 10000, step: 0.01, label: 'Height' },
    fill:   { dataType: 'color', value: { r: 1, g: 1, b: 1, a: 1 }, label: 'Fill' },
  }),
  ...internals({
    polylines: { dataType: 'polylines', isOutput: true, allowInput: false, label: 'Polylines' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class Rectangle2DNode extends BaseNode<typeof rectangle2DEndpoints> {
  private cachedPolylines: Polylines = [];

  initialize(): void {
    super.initialize();
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const w = this.params.width.value;
    const h = this.params.height.value;
    const hw = w / 2;
    const hh = h / 2;

    const points = [
      { x: -hw, y: -hh, z: 0 },
      { x:  hw, y: -hh, z: 0 },
      { x:  hw, y:  hh, z: 0 },
      { x: -hw, y:  hh, z: 0 },
      { x: -hw, y: -hh, z: 0 },  // close the polygon
    ];

    const fill = colorToHex(this.params.fill.value);
    this.cachedPolylines = [{ points, fill }];
    this.internals.polylines.set(this.cachedPolylines);
  }

  /** Duck-typed accessor for polyline adapter */
  getPolylines(): Polylines {
    return this.cachedPolylines;
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const rectangle2DNodeDef: NodeDef<Rectangle2DNode> = {
  type: 'Rectangle2D',
  shortName: 'rect2d',
  info: 'Outputs a filled rectangle as a styled polyline for 2D rendering.',
  domain: Domain.Data,
  category: 'Shape',
  endpoints: rectangle2DEndpoints,
  ctor: Rectangle2DNode,
};
